import { useEffect, useMemo, useState } from 'react';

import { AssetColumnId, GetIssuesResponseDTO, GetWorkOrdersRequestDTO, GetWorkOrdersResponseDTO, MODULE, RecordState, WorkOrderStatus } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useLocation, useParams } from 'react-router-dom';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import { FeedbackPrompt } from '@/components/feedback/feedback-prompt';
import ArchiveIcon from '@/components/icons/archive';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import FolderOpenIcon from '@/components/icons/folder-open';
import GearIcon from '@/components/icons/gear';
import TagIcon from '@/components/icons/tag';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import WorkOrderDrawer from '@/components/work-orders/work-order-drawer';
import { useFeedbackPrompt } from '@/hooks/use-feedback-prompt';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { getAssetByIdQueryOptions, tagKeys } from '@/queries/assets';
import { buildFilterFields } from '@/queries/column-filter';
import { awaWorkOrderKeys, getWorkOrdersInfiniteQueryOptions, workOrderKeys } from '@/queries/work-orders';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';

import { BulkUpdateWorkOrderTagsDialog } from '../../../work-orders/bulk-update-work-order-tags-dialog';
import { UpdateWorkOrderStatusDialog } from '../../../work-orders/update-work-order-status-dialog';
import { AssetWorkOrdersTable, NoWorkOrdersCreatedDisplay, NoWorkOrdersFoundFromFiltersDisplay, NoWorkOrdersFoundFromSearchDisplay } from './table';
import { columns, FILTER_FIELD_MAP, groupOptions } from './table-meta';

const AssetWorkOrdersPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isAdmin, canExportCsv } = usePermissions();
  const locationState = useLocation().state as
    | {
        workOrder?: GetWorkOrdersResponseDTO['data'][number];
        issue?: GetIssuesResponseDTO['data'][number];
      }
    | undefined;
  const workOrderFromLocation = locationState?.workOrder;
  const issueFromLocation = locationState?.issue;

  const { setModuleFilters } = useModulesStore();

  const { triggerForWorkOrder, promptProps: feedbackPromptProps } = useFeedbackPrompt('work-orders');

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.AssetWorkOrders, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns,
  });

  const [openWorkOrderDrawer, setOpenWorkOrderDrawer] = useState(!!workOrderFromLocation || !!issueFromLocation);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<GetWorkOrdersResponseDTO['data'][number] | null>(
    workOrderFromLocation || issueFromLocation?.workOrder || null,
  );
  const [selectedIssue, setSelectedIssue] = useState<GetIssuesResponseDTO['data'][number] | null>(issueFromLocation || null);
  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [openStatusUpdate, setOpenStatusUpdate] = useState(false);
  const [openWorkOrderTagsUpdate, setOpenWorkOrderTagsUpdate] = useState(false);
  const [search, setSearch] = useState('');
  const [workOrderState, setWorkOrderState] = useState<RecordState>(RecordState.Active);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);

  useEffect(() => {
    if (locationState) {
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = useDebounce(search, 500);

  const { data: asset } = useQuery(getAssetByIdQueryOptions({ assetId: id }));

  const {
    data: workOrdersData,
    fetchNextPage: fetchNextWorkOrdersPage,
    hasNextPage: hasNextWorkOrdersPage,
    isFetching: isFetchingWorkOrders,
    isFetchingNextPage: isFetchingNextWorkOrdersPage,
    error: workOrdersError,
  } = useInfiniteQuery(
    getWorkOrdersInfiniteQueryOptions({
      search: debouncedSearch,
      state: workOrderState,
      filters: buildFilterFields(filters, FILTER_FIELD_MAP, {
        transformValue: (columnId, raw) => (columnId === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
        extraFields: { assetIds: [id as string] },
      }),
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetWorkOrdersRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetWorkOrdersRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextWorkOrdersPage,
    hasNextPage: hasNextWorkOrdersPage,
    isFetching: isFetchingWorkOrders,
    isError: !!workOrdersError,
    root: scrollRoot,
  });

  const getSelectedWorkOrders = useMemo(() => {
    return (incomingData: typeof workOrdersData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedWorkOrders = useMemo(() => getSelectedWorkOrders(workOrdersData), [workOrdersData, getSelectedWorkOrders]);

  const { mutateAsync: bulkUpdateWorkOrderStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: async (status: WorkOrderStatus) => {
      const ids = selectedWorkOrders.map((wo) => wo.id);
      return ApiService.BulkUpdateWorkOrderStatus({ ids, status });
    },
    onSuccess: (_, status) => {
      toast.success('Work orders updated successfully');
      setOpenStatusUpdate(false);
      const closedWorkOrders = status === WorkOrderStatus.Closed ? [...selectedWorkOrders] : [];
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
      if (closedWorkOrders.length > 0) {
        // Attribute the prompt to the first selected WO — the rating is about the close-out experience, not a specific entity.
        triggerForWorkOrder(closedWorkOrders[0]);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { mutateAsync: bulkUpdateWorkOrderTags, isPending: isUpdatingWorkOrderTags } = useMutation({
    mutationFn: async (tags: string[]) => {
      const ids = selectedWorkOrders.map((wo) => wo.id);
      return ApiService.BulkUpdateWorkOrderTags({ workOrderIds: ids, tags });
    },
    onSuccess: () => {
      toast.success('Work orders updated successfully');
      setOpenWorkOrderTagsUpdate(false);
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleUpdateWorkOrderTags = async (tags: string[], resetForm: () => void) => {
    await bulkUpdateWorkOrderTags(tags);
    resetForm();
  };

  const { mutateAsync: bulkUpdateWorkOrderState, isPending: isUpdatingWorkOrderState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState }) => await ApiService.BulkUpdateWorkOrderState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Work orders restored successfully',
        [RecordState.Archived]: 'Work orders archived successfully',
        [RecordState.Deleted]: 'Work orders deleted successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenDeleteConfirmation(false);
      setOpenRestoreConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleBulkArchive() {
    bulkUpdateWorkOrderState({ ids: selectedWorkOrders.map((wo) => wo.id), state: RecordState.Archived });
  }

  function handleBulkDelete() {
    bulkUpdateWorkOrderState({ ids: selectedWorkOrders.map((wo) => wo.id), state: RecordState.Deleted });
  }

  function handleBulkRestore() {
    bulkUpdateWorkOrderState({ ids: selectedWorkOrders.map((wo) => wo.id), state: RecordState.Active });
  }

  function handleCsvExport() {
    if (!workOrdersData?.pages) return;

    const selected = getSelectedWorkOrders(workOrdersData);

    const columns = columnOrder.filter((column) => column.id !== 'select' && column.active).map((column) => column.id);
    openExportTab({
      kind: 'csv',
      request: {
        queryType: 'work-orders',
        filename: buildCsvExportFilename('work-orders'),
        filters: { state: workOrderState, fields: { id: selected.map((workOrder) => workOrder.id) } },
        groupBy: grouping.length
          ? { field: grouping[0] as NonNullable<GetWorkOrdersRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
          : undefined,
        orderBy: sorting.key
          ? { field: sorting.key as NonNullable<GetWorkOrdersRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
          : undefined,
        // An empty array fails csvExportColumnsSchema's `.min(1)`; omit it so the server falls back to the canonical column set.
        columns: columns.length > 0 ? columns : undefined,
      },
    });
  }

  useEffect(() => {
    if (workOrdersError) {
      toast.error(workOrdersError.message);
    }
  }, [toast, workOrdersError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => workOrdersData?.pages.flatMap((page) => page.data) ?? [], [workOrdersData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedWorkOrders.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={workOrderState}
                    onChange={(value) => setWorkOrderState(value as RecordState)}
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.AssetWorkOrders, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex h-full items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<GetWorkOrdersResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      defaultSectionLabel="Work Order"
                      onOpenChange={setOpenColumnsDialog}
                      columns={columns}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: AssetColumnId; active: boolean }> },
                        })
                      }
                    />
                    <WorkOrderDrawer
                      open={openWorkOrderDrawer}
                      onOpenChange={(open) => {
                        setOpenWorkOrderDrawer(open);
                        if (!open) {
                          setSelectedWorkOrder(null);
                          setSelectedIssue(null);
                        }
                      }}
                      workOrderId={selectedWorkOrder?.id}
                      issue={selectedIssue}
                      asset={asset}
                      registerState={workOrderState}
                      onCloseSuccess={triggerForWorkOrder}
                    />
                  </div>
                </div>
              </>
            )}
            {selectedWorkOrders.length > 0 && (
              <div className="flex min-h-10 items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedWorkOrders.length} totalCount={workOrdersData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                <TablePageHeaderActionButton icon={<GearIcon />} label="Edit Status" onClick={() => setOpenStatusUpdate(true)} />
                <TablePageHeaderActionButton icon={<TagIcon />} label="Edit Tags" onClick={() => setOpenWorkOrderTagsUpdate(true)} />
                {isAdmin && workOrderState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {isAdmin && workOrderState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      label="Delete Selected"
                      onClick={() => setOpenDeleteConfirmation(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <AssetWorkOrdersTable
          data={flattenedData}
          loading={(isFetchingWorkOrders && !isFetchingNextWorkOrdersPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoWorkOrdersFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoWorkOrdersFoundFromFiltersDisplay
                openWorkOrderDrawer={() => setOpenWorkOrderDrawer(true)}
                removeColumnFilters={() => setModuleFilters(MODULE.AssetWorkOrders, [])}
              />
            ) : (
              <NoWorkOrdersCreatedDisplay openWorkOrderDrawer={() => setOpenWorkOrderDrawer(true)} />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.AssetWorkOrders, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onRowClick={(row) => {
            setSelectedWorkOrder(row);
            setOpenWorkOrderDrawer(true);
          }}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextWorkOrdersPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextWorkOrdersPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <UpdateWorkOrderStatusDialog open={openStatusUpdate} onOpenChange={setOpenStatusUpdate} callback={bulkUpdateWorkOrderStatus} loading={isUpdatingStatus} />
      <BulkUpdateWorkOrderTagsDialog
        open={openWorkOrderTagsUpdate}
        onOpenChange={setOpenWorkOrderTagsUpdate}
        callback={handleUpdateWorkOrderTags}
        loading={isUpdatingWorkOrderTags}
      />
      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleBulkArchive}
        loading={isUpdatingWorkOrderState}
        title="Archive work orders?"
        description="Are you sure you want to archive the selected work orders? Open tasks will be removed. You can restore them later from the archived tab."
      />
      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleBulkDelete}
        loading={isUpdatingWorkOrderState}
        title="Delete work orders?"
        description="Are you sure you want to delete the selected work orders? This action cannot be undone."
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleBulkRestore}
        loading={isUpdatingWorkOrderState}
        title="Restore work orders?"
        description="Are you sure you want to restore the selected work orders? They will be moved back to the active tab."
      />

      <FeedbackPrompt {...feedbackPromptProps} />
    </div>
  );
};

export default AssetWorkOrdersPage;
